import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, query, where,
  addDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';

// ─── Canvas block definitions (Osterwalder layout) ───────────
const BLOCKS = [
  { id: 'key_partnerships',    label: 'Key Partnerships',    short: 'KP',  color: '#7c6fcd', placeholder: 'Who are your key partners and suppliers? What key resources or activities do they provide?' },
  { id: 'key_activities',      label: 'Key Activities',      short: 'KA',  color: '#4a7c3f', placeholder: 'What key activities does your value proposition require? Distribution channels, customer relationships, revenue streams?' },
  { id: 'key_resources',       label: 'Key Resources',       short: 'KR',  color: '#2a9d8f', placeholder: 'What key resources does your value proposition require? Physical, intellectual, human, financial?' },
  { id: 'value_propositions',  label: 'Value Propositions',  short: 'VP',  color: '#4a5481', placeholder: 'What value do you deliver to customers? Which customer problems are you solving? Which needs are you satisfying?' },
  { id: 'customer_relationships', label: 'Customer Relationships', short: 'CR', color: '#e8522a', placeholder: 'What type of relationship does each customer segment expect? Personal assistance, self-service, automated, community?' },
  { id: 'channels',            label: 'Channels',            short: 'CH',  color: '#f4a261', placeholder: 'Through which channels do your customer segments want to be reached? How do you reach them now?' },
  { id: 'customer_segments',   label: 'Customer Segments',   short: 'CS',  color: '#e76f51', placeholder: 'For whom are you creating value? Who are your most important customers? Mass market, niche, segmented, diversified?' },
  { id: 'cost_structure',      label: 'Cost Structure',      short: 'C$',  color: '#6b6b6b', placeholder: 'What are the most important costs in your business model? Fixed/variable costs, economies of scale, scope?' },
  { id: 'revenue_streams',     label: 'Revenue Streams',     short: 'R$',  color: '#1a936f', placeholder: 'For what value are customers willing to pay? How do they currently pay? How much does each stream contribute?' },
];

// Classic Osterwalder grid layout
// Row 1: KP | KA+KR | VP | CR+CH | CS  (top row)
// Row 2: C$ (left half) | R$ (right half)
const GRID_LAYOUT = [
  { blockId: 'key_partnerships',      row: 1, col: 1, colSpan: 1, rowSpan: 2 },
  { blockId: 'key_activities',        row: 1, col: 2, colSpan: 1, rowSpan: 1 },
  { blockId: 'key_resources',         row: 2, col: 2, colSpan: 1, rowSpan: 1 },
  { blockId: 'value_propositions',    row: 1, col: 3, colSpan: 1, rowSpan: 2 },
  { blockId: 'customer_relationships',row: 1, col: 4, colSpan: 1, rowSpan: 1 },
  { blockId: 'channels',              row: 2, col: 4, colSpan: 1, rowSpan: 1 },
  { blockId: 'customer_segments',     row: 1, col: 5, colSpan: 1, rowSpan: 2 },
  { blockId: 'cost_structure',        row: 3, col: 1, colSpan: 2, rowSpan: 1 },
  { blockId: 'revenue_streams',       row: 3, col: 4, colSpan: 2, rowSpan: 1 },
];

const ROLE_LABELS = { team: 'Team', coach: 'Coach', beneficiary: 'Company', client: 'Client' };

export default function CanvasPage() {
  const { profile } = useAuth();
  const [companyId, setCompanyId]     = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [versions, setVersions]       = useState([]);
  const [activeVersion, setActiveVersion] = useState(null);
  const [blocks, setBlocks]           = useState({});
  const [drafts, setDrafts]           = useState({});
  const [comments, setComments]       = useState({});
  const [activeComment, setActiveComment] = useState(null); // blockId with open sticky
  const [commentDrafts, setCommentDrafts] = useState({});
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);

  const isReadOnly = profile?.role === 'client' || profile?.role === 'team';

  // Load company
  useEffect(() => {
    if (!profile?.uid) return;
    async function loadCompany() {
      try {
        let cId = null;
        let cName = '';

        if (profile.role === 'beneficiary') {
          const q = query(collection(db, 'companies'), where('memberUids', 'array-contains', profile.uid));
          const snap = await getDocs(q);
          if (!snap.empty) { cId = snap.docs[0].id; cName = snap.docs[0].data().name; }
        } else if (profile.role === 'coach') {
          const q = query(collection(db, 'companies'), where('coachId', '==', profile.uid));
          const snap = await getDocs(q);
          if (!snap.empty) { cId = snap.docs[0].id; cName = snap.docs[0].data().name; }
        }

        if (cId) { setCompanyId(cId); setCompanyName(cName); }
      } catch (err) { console.error(err); }
    }
    loadCompany();
  }, [profile]);

  // Load canvas versions
  useEffect(() => {
    if (!companyId) return;
    async function loadCanvas() {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'companies', companyId, 'canvasVersions'),
            where('type', '==', 'bmc')
          )
        );
        const vList = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a,b) => {
            const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const db2 = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return db2 - da;
          });
        setVersions(vList);

        if (vList.length > 0) {
          setActiveVersion(vList[0]);
          setBlocks(vList[0].blocks || {});
          setDrafts(vList[0].blocks || {});
          if (vList.length > 1) setShowHistory(true); // auto-open if multiple versions
        }

        // Load comments for each block
        await loadComments(companyId);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    loadCanvas();
  }, [companyId]);

  const loadComments = async (cId) => {
    try {
       const snap = await getDocs(collection(db, 'companies', cId, 'canvasComments'));
      const commentMap = {};
      const sortedDocs = snap.docs.sort((a,b) => {
        const da = a.data().createdAt?.toDate ? a.data().createdAt.toDate() : new Date(0);
        const db2 = b.data().createdAt?.toDate ? b.data().createdAt.toDate() : new Date(0);
        return da - db2;
      });
      sortedDocs.forEach(d => {
        const data = { id: d.id, ...d.data() };
        if (!commentMap[data.blockId]) commentMap[data.blockId] = [];
        commentMap[data.blockId].push(data);
      });
      setComments(commentMap);
    } catch (err) { console.error(err); }
  };

  // Save new canvas version
  const handleSave = async () => {
    if (!companyId || saving) return;
    setSaving(true);
    try {
      const versionData = {
        companyId,
        type:      'bmc',
        blocks:    drafts,
        status:    'draft',
        savedBy:   profile.uid,
        savedByName: profile.displayName,
        createdAt: serverTimestamp(),
      };
      const ref = await addDoc(
        collection(db, 'companies', companyId, 'canvasVersions'),
        versionData
      );
      const newVersion = { id: ref.id, ...versionData, createdAt: new Date() };
      setVersions(prev => [newVersion, ...prev]);
      setActiveVersion(newVersion);
      setBlocks(drafts);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  // Post comment on a block
  const postComment = async (blockId) => {
    const text = commentDrafts[blockId]?.trim();
    if (!text || !companyId) return;
    try {
      const commentData = {
        blockId,
        body:       text,
        authorUid:  profile.uid,
        authorName: profile.displayName,
        authorRole: profile.role,
        createdAt:  serverTimestamp(),
      };
      const ref = await addDoc(
        collection(db, 'companies', companyId, 'canvasComments'),
        commentData
      );
      setComments(prev => ({
        ...prev,
        [blockId]: [...(prev[blockId] || []), { id: ref.id, ...commentData, createdAt: new Date() }]
      }));
      setCommentDrafts(prev => ({ ...prev, [blockId]: '' }));
    } catch (err) { console.error(err); }
  };

  const loadVersion = (version) => {
    // Show preview first — user can choose to restore
    setPreviewVersion(version);
  };

  const restoreVersion = (version) => {
    setActiveVersion(version);
    setBlocks(version.blocks || {});
    setDrafts(version.blocks || {});
    setPreviewVersion(null);
  };

  const hasChanges = JSON.stringify(drafts) !== JSON.stringify(blocks);

  if (loading) return (
    <DashboardLayout>
      <div className="gm-loading"><div className="gm-spinner" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="gm-page gm-canvas-page">
        <div className="gm-page-header">
          <div>
            <h1 className="gm-page-title">Business Model Canvas</h1>
            <p className="gm-page-sub">{companyName}</p>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => setShowHistory(!showHistory)}>
                {showHistory ? 'Hide history' : versions.length > 0 ? `History (${versions.length})` : 'No versions yet'}
              </button>
            {!isReadOnly && (
              <button
                className="gm-btn gm-btn-primary"
                onClick={handleSave}
                disabled={saving || !hasChanges}
              >
                {saving ? 'Saving…' : 'Save version'}
              </button>
            )}
            {saved && <span className="gm-save-success">✓ Version saved</span>}
          </div>
        </div>

        {/* Version history panel */}
        {showHistory && (
          <div className="gm-canvas-history">
            <h3>Version history</h3>
            {versions.map((v, idx) => {
              const date = v.createdAt?.toDate ? v.createdAt.toDate() : new Date(v.createdAt);
              return (
                <div key={v.id}
                  className={`gm-canvas-history-item ${activeVersion?.id === v.id ? 'gm-canvas-history-item--active' : ''}`}
                  onClick={() => loadVersion(v)}
                >
                  <span className="gm-canvas-history-date">
                    {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="gm-canvas-history-by">by {v.savedByName}</span>
                  {idx === 0 && <span className="gm-validated-badge">Latest</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Version preview modal */}
        {previewVersion && (
          <div className="gm-canvas-preview-overlay" onClick={() => setPreviewVersion(null)}>
            <div className="gm-canvas-preview-modal" onClick={e => e.stopPropagation()}>
              <div className="gm-canvas-preview-header">
                <div>
                  <div className="gm-canvas-preview-title">Version preview</div>
                  <div className="gm-canvas-preview-meta">
                    {previewVersion.createdAt?.toDate
                      ? previewVersion.createdAt.toDate().toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
                      : ''} · saved by {previewVersion.savedByName}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={() => restoreVersion(previewVersion)}>
                    Restore this version
                  </button>
                  <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => setPreviewVersion(null)}>
                    Close
                  </button>
                </div>
              </div>
              <div className="gm-canvas-preview-body">
                <CanvasPreviewGrid blocks={previewVersion.blocks || {}} />
              </div>
            </div>
          </div>
        )}

        {/* Canvas grid */}
        <div className="gm-bmc-grid">
          {GRID_LAYOUT.map(({ blockId, row, col, colSpan, rowSpan }) => {
            const block = BLOCKS.find(b => b.id === blockId);
            const blockComments = comments[blockId] || [];
            const isCommentOpen = activeComment === blockId;
            const draft = drafts[blockId] || '';

            return (
              <div
                key={blockId}
                className="gm-bmc-block"
                style={{
                  gridColumn: `${col} / span ${colSpan}`,
                  gridRow:    `${row} / span ${rowSpan}`,
                  borderTop:  `3px solid ${block.color}`,
                }}
              >
                {/* Block header */}
                <div className="gm-bmc-block-header">
                  <span className="gm-bmc-block-short" style={{ color: block.color }}>{block.short}</span>
                  <span className="gm-bmc-block-label">{block.label}</span>
                  {/* Comment toggle */}
                  <button
                    className={`gm-bmc-comment-btn ${blockComments.length > 0 ? 'gm-bmc-comment-btn--has' : ''}`}
                    onClick={() => setActiveComment(isCommentOpen ? null : blockId)}
                    title={`${blockComments.length} comment${blockComments.length !== 1 ? 's' : ''}`}
                  >
                    💬 {blockComments.length > 0 ? blockComments.length : ''}
                  </button>
                </div>

                {/* Block content */}
                {isReadOnly ? (
                  <div className="gm-bmc-block-text">{draft || <span className="gm-bmc-block-empty">Not yet filled in</span>}</div>
                ) : (
                  <textarea
                    className="gm-bmc-block-textarea"
                    placeholder={block.placeholder}
                    value={draft}
                    onChange={e => setDrafts(prev => ({ ...prev, [blockId]: e.target.value }))}
                  />
                )}

                {/* Sticky comment panel */}
                {isCommentOpen && (
                  <div className="gm-bmc-sticky">
                    <div className="gm-bmc-sticky-header">
                      <span>Comments — {block.label}</span>
                      <button className="gm-btn-icon" onClick={() => setActiveComment(null)}>✕</button>
                    </div>
                    <div className="gm-bmc-sticky-comments">
                      {blockComments.length === 0 && (
                        <p className="gm-comments-empty">No comments yet.</p>
                      )}
                      {blockComments.map((c, idx) => {
                        const date = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
                        return (
                          <div key={c.id || idx} className="gm-comment">
                            <div className="gm-comment-meta">
                              <span className="gm-comment-author">{c.authorName}</span>
                              <span className={`gm-comment-role gm-action-assignee--${c.authorRole}`}>
                                {ROLE_LABELS[c.authorRole] || c.authorRole}
                              </span>
                              <span className="gm-comment-time">
                                {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                              </span>
                            </div>
                            <div className="gm-comment-body">{c.body}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="gm-comment-form">
                      <textarea
                        placeholder="Add a comment…"
                        value={commentDrafts[blockId] || ''}
                        onChange={e => setCommentDrafts(prev => ({ ...prev, [blockId]: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postComment(blockId); }}}
                        rows={2}
                      />
                      <button
                        className="gm-btn gm-btn-primary gm-btn-sm"
                        onClick={() => postComment(blockId)}
                        disabled={!commentDrafts[blockId]?.trim()}
                      >
                        Post
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!isReadOnly && hasChanges && (
          <div className="gm-canvas-save-bar">
            <span>You have unsaved changes</span>
            <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save version'}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// ─── Canvas preview grid (read-only, used in version history modal) ──
const PREVIEW_BLOCKS = [
  { id:'key_partnerships',      label:'Key Partnerships',      short:'KP', color:'#7c6fcd' },
  { id:'key_activities',        label:'Key Activities',        short:'KA', color:'#4a7c3f' },
  { id:'key_resources',         label:'Key Resources',         short:'KR', color:'#2a9d8f' },
  { id:'value_propositions',    label:'Value Propositions',    short:'VP', color:'#4a5481' },
  { id:'customer_relationships',label:'Customer Relationships',short:'CR', color:'#e8522a' },
  { id:'channels',              label:'Channels',              short:'CH', color:'#f4a261' },
  { id:'customer_segments',     label:'Customer Segments',     short:'CS', color:'#e76f51' },
  { id:'cost_structure',        label:'Cost Structure',        short:'C$', color:'#6b6b6b' },
  { id:'revenue_streams',       label:'Revenue Streams',       short:'R$', color:'#1a936f' },
];

const PREVIEW_LAYOUT = [
  { blockId:'key_partnerships',      gridColumn:'1',          gridRow:'1 / span 2' },
  { blockId:'key_activities',        gridColumn:'2',          gridRow:'1' },
  { blockId:'key_resources',         gridColumn:'2',          gridRow:'2' },
  { blockId:'value_propositions',    gridColumn:'3',          gridRow:'1 / span 2' },
  { blockId:'customer_relationships',gridColumn:'4',          gridRow:'1' },
  { blockId:'channels',              gridColumn:'4',          gridRow:'2' },
  { blockId:'customer_segments',     gridColumn:'5',          gridRow:'1 / span 2' },
  { blockId:'cost_structure',        gridColumn:'1 / span 2', gridRow:'3' },
  { blockId:'revenue_streams',       gridColumn:'4 / span 2', gridRow:'3' },
];

function CanvasPreviewGrid({ blocks }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gridTemplateRows:'auto auto auto', gap:4 }}>
      {PREVIEW_LAYOUT.map(({ blockId, gridColumn, gridRow }) => {
        const block = PREVIEW_BLOCKS.find(b => b.id === blockId);
        const text  = blocks[blockId];
        return (
          <div key={blockId} style={{
            gridColumn, gridRow,
            background:'#fff',
            border:'1px solid #e2e8f0',
            borderTop:`3px solid ${block.color}`,
            borderRadius:6,
            padding:10,
            minHeight:100,
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:6 }}>
              <span style={{ fontSize:'0.65rem', fontWeight:800, color:block.color }}>{block.short}</span>
              <span style={{ fontSize:'0.65rem', fontWeight:700, color:'#475569' }}>{block.label}</span>
            </div>
            <div style={{ fontSize:'0.75rem', color: text ? '#1e293b' : '#cbd5e1', lineHeight:1.5, whiteSpace:'pre-wrap' }}>
              {text || 'Empty'}
            </div>
          </div>
        );
      })}
    </div>
  );
}
